import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormHelperText from '@material-ui/core/FormHelperText';
import { useTranslation } from 'utils/modifiedTranslation';
import useStyles from './styles';

/**
 * A text input for user's answer
 */
const TextInput = React.forwardRef((props, ref) => {
  const {
    type,
    isRequired,
    isInvalid,
    multiline,
    placeholder,
    value,
    unit,
    step,
    label,
    helperText,
    rootClassName,
    onChange,
    wrapperClassName,
    labelClassName,
    inputClassName,
    touched,
    error,
    dinamicTranslationKey,
    shouldNotTranslate,
    autoComplete,
    min = 0,
    max,
  } = props;
  const { t } = useTranslation();
  const { appConfiguration: { providerSpecific } = {} } = useSelector((state) => state) ?? {};
  const classes = useStyles({ provider: providerSpecific?.code });

  const handleChange = (event) => {
    onChange(event.target.value);
  };
  const translateValue = shouldNotTranslate
    ? value
    : dinamicTranslationKey
      ? t(value, dinamicTranslationKey)
      : t(value);
  const isInputError = isInvalid ? classes.inputError : '';
  const isInputErrorMessage = isInvalid ? classes.redText : '';
  return (
    <div className={wrapperClassName || classes.errorWrapper}>
      {label.length > 0 && (
        <InputLabel className={cx(classes.inputLabel, labelClassName)}>
          {t(label)}
          {' '}
          {isRequired && <span className={classes.required}> *</span>}
        </InputLabel>
      )}
      <Input
        inputRef={
          (inputElement) => {
            if (ref && inputElement) {
              if (typeof ref === 'function') {
                ref(inputElement);
              } else {
                ref.current = inputElement;
              }
            }
          }
        }
        required={isRequired}
        error={isInvalid}
        type={type}
        multiline={multiline}
        placeholder={t(placeholder)}
        className={cx(classes.inputWrapper, rootClassName, isInputError)}
        classes={{
          input: cx(classes.input, inputClassName),
        }}
        value={translateValue ?? ''}
        onChange={handleChange}
        inputProps={{
          min,
          max,
          step,
        }}
        endAdornment={unit && <InputAdornment position="end">{t(unit)}</InputAdornment>}
        autoComplete={autoComplete}
      />
      {helperText?.length > 0 && (
        <FormHelperText className={`${classes.helperText} ${isInputErrorMessage}`}>{t(helperText)}</FormHelperText>
      )}
      {touched && error && <span className={classes.redError}>{error}</span>}
    </div>
  );
});

TextInput.propTypes = {
  /**
   * Type of the input.
   */
  type: PropTypes.string,
  /**
   * Type of the input.
   */
  autoComplete: PropTypes.string,
  /**
   * Flag to make input required to be filled.
   */
  isRequired: PropTypes.bool,
  /**
   * Flag to set error behaviour.
   */
  isInvalid: PropTypes.bool,
  /**
   * Flag to render textarea
   */
  multiline: PropTypes.bool,
  /**
   * Label of the input.
   */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /**
   * Placeholder of the input.
   */
  placeholder: PropTypes.string,
  /**
   * value of the input.
   */
  value: PropTypes.string,
  /**
   * String for displaying on the right side in the input
   */
  unit: PropTypes.string,
  /**
   * Step to increase value in input
   */
  step: PropTypes.number,
  /**
   * String for displaying under the input
   */
  helperText: PropTypes.string,
  /**
   * Classname for the root div
   */
  rootClassName: PropTypes.string,
  /**
   * Classname for the root div
   */
  wrapperClassName: PropTypes.string,
  /**
   * Classname for the label
   */
  labelClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  /**
   * Function to call when input filled
   */
  onChange: PropTypes.func,
  /**
   * Object for dynamic number of rows
   */
  dinamicTranslationKey: PropTypes.object,
  /**
   * Object for dynamic number of rows
   */
  touched: PropTypes.bool,
  /**
   * Object for dynamic number of rows
   */
  error: PropTypes.bool,
  shouldNotTranslate: PropTypes.any,
  min: PropTypes.number,
  max: PropTypes.number,
};

TextInput.defaultProps = {
  type: 'number',
  placeholder: '',
  touched: false,
  error: false,
  isRequired: false,
  isInvalid: false,
  multiline: false,
  label: '',
  step: 0.1,
  unit: null,
  helperText: '',
  rootClassName: '',
  wrapperClassName: '',
  onChange: () => { },
  value: '',
  dinamicTranslationKey: null,
};

export default TextInput;
